import { normalizePubNubTimetoken } from './pubnubTimetoken';

export const RECALL_MESSAGE_EVENT = 'RECALL_MESSAGE';

const DEFAULT_PUBNUB_SYSTEM_BACKEND_ID = 'system-backend';

const runtimeTrustedRecallPublishers = new Set<string>();

function parseTrustedRecallPublisherEnv(): string[] {
  const raw = process.env.REACT_APP_PUBNUB_SYSTEM_BACKEND_ID?.trim();
  if (!raw) return [];

  return raw
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
}

export function registerTrustedRecallPublisher(
  publisherId: string | undefined | null,
): void {
  const normalized = publisherId?.trim();
  if (!normalized) return;
  runtimeTrustedRecallPublishers.add(normalized);
}

export function getTrustedRecallPublishers(): Set<string> {
  const publishers = new Set<string>(runtimeTrustedRecallPublishers);

  for (const publisherId of parseTrustedRecallPublisherEnv()) {
    publishers.add(publisherId);
  }

  if (publishers.size === 0) {
    publishers.add(DEFAULT_PUBNUB_SYSTEM_BACKEND_ID);
  }

  return publishers;
}

export function getPubNubSystemBackendId(): string {
  const publishers = Array.from(getTrustedRecallPublishers());
  return publishers[publishers.length - 1] ?? DEFAULT_PUBNUB_SYSTEM_BACKEND_ID;
}

export function isTrustedRecallPublisher(
  publisher: string | undefined | null,
): boolean {
  if (!publisher) return false;
  return getTrustedRecallPublishers().has(publisher);
}

export type RecallMessageNotification = {
  event?: string;
  channel?: string;
  timetoken?: string | number;
};

export function isMessageRecalledByPubNub(
  actions?: Record<string, Record<string, unknown>>,
): boolean {
  const deleted = actions?.deleted;
  if (!deleted || typeof deleted !== 'object') return false;
  return Object.keys(deleted).length > 0;
}

/**
 * Mark channel-list last-message entries as recalled, given (channelId,
 * timetoken) pairs reported by the backend recall-status endpoint. Only flips an
 * entry when its newest timetoken matches a recalled one, so a newer non-recalled
 * message keeps its preview. Returns the same reference when nothing changed.
 */
export function applyRecallStatusToLastMessages<
  T extends { timetoken: string | number; recalled?: boolean },
>(
  prev: Record<string, T>,
  recalledPairs: Array<{ channelId: string; timetoken: string | number }>,
): Record<string, T> {
  if (recalledPairs.length === 0) return prev;

  const recalledTokensByChannel = new Map<string, Set<string>>();
  for (const pair of recalledPairs) {
    const tokens = recalledTokensByChannel.get(pair.channelId) ?? new Set();
    tokens.add(normalizePubNubTimetoken(pair.timetoken));
    recalledTokensByChannel.set(pair.channelId, tokens);
  }

  let changed = false;
  const next: Record<string, T> = { ...prev };
  for (const [channelId, tokens] of recalledTokensByChannel) {
    const entry = next[channelId];
    if (!entry || entry.recalled) continue;
    if (tokens.has(normalizePubNubTimetoken(entry.timetoken))) {
      next[channelId] = { ...entry, recalled: true };
      changed = true;
    }
  }

  return changed ? next : prev;
}

export function getRecallMessageNotification(
  payload: unknown,
): RecallMessageNotification | null {
  if (!payload || typeof payload !== 'object') return null;

  const notification = payload as RecallMessageNotification;
  if (notification.event !== RECALL_MESSAGE_EVENT) return null;
  if (notification.timetoken == null) return null;

  return notification;
}

export function getTrustedRecallMessageNotification(
  payload: unknown,
  publisher: string | undefined | null,
): RecallMessageNotification | null {
  const notification = getRecallMessageNotification(payload);
  if (!notification) return null;
  if (!isTrustedRecallPublisher(publisher)) return null;
  return notification;
}

export function isPubNubMessageDeletedAction(event: {
  channel: string;
  event?: string;
  data?: { type?: string };
  messageTimetoken?: string | number;
}): event is {
  channel: string;
  event: string;
  data: { type: string };
  messageTimetoken: string | number;
} {
  return (
    event.event === 'added' &&
    event.data?.type === 'deleted' &&
    event.messageTimetoken != null
  );
}

export function isRecallMessageNotification(payload: unknown): boolean {
  return getRecallMessageNotification(payload) !== null;
}

/** UUID channel notifications that should affect unread / last-message preview. */
export function isIncomingChannelNotification(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object') return false;

  const notification = payload as { channel?: string };
  if (!notification.channel) return false;

  return !isRecallMessageNotification(payload);
}

export function isRelevantRecallForChat(params: {
  notification: RecallMessageNotification;
  eventChannel: string;
  chatChannel: string;
  currentUserUuid?: string | null;
}): boolean {
  const { notification, eventChannel, chatChannel, currentUserUuid } = params;

  // The recall payload must target the open chat, regardless of which channel
  // delivered it. Otherwise a trusted recall for another channel arriving on the
  // chat channel could hide an unrelated timetoken in the active thread.
  if (notification.channel !== chatChannel) return false;

  // And it must arrive via a channel that legitimately carries this chat's
  // events: the chat channel itself or the current user's notification channel.
  return eventChannel === chatChannel || eventChannel === currentUserUuid;
}

export function removeMessageByTimetoken<T extends { timetoken: string | number }>(
  messages: T[],
  timetoken: string | number,
): T[] {
  const normalized = normalizePubNubTimetoken(timetoken);

  return messages.filter(
    (msg) => normalizePubNubTimetoken(msg.timetoken) !== normalized,
  );
}

export function rememberRecalledTimetoken(
  recalledTimetokens: Set<string>,
  timetoken: string | number,
): string {
  const normalized = normalizePubNubTimetoken(timetoken);
  recalledTimetokens.add(normalized);
  return normalized;
}

export function markMessageRecalled<
  T extends { timetoken: string | number; recalled?: boolean },
>(
  recalledTimetokens: Set<string>,
  setMessages: (update: (prev: T[]) => T[]) => void,
  timetoken: string | number,
): void {
  const normalized = rememberRecalledTimetoken(recalledTimetokens, timetoken);
  setMessages((prev) =>
    prev.map((msg) =>
      normalizePubNubTimetoken(msg.timetoken) === normalized
        ? { ...msg, recalled: true }
        : msg,
    ),
  );
}

/** Male app: hide recalled messages from the list. */
export function applyMessageRecallForMale<
  T extends { timetoken: string | number },
>(
  recalledTimetokens: Set<string>,
  setMessages: (update: (prev: T[]) => T[]) => void,
  timetoken: string | number,
): void {
  rememberRecalledTimetoken(recalledTimetokens, timetoken);
  setMessages((prev) => removeMessageByTimetoken(prev, timetoken));
}